import { Link, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { ArrowLeft, Save } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useFormSuccess } from "@/shared/hooks/useFormSuccess";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, textareaClass } from "../components/FormField";
import { submitAuditorAuditReport } from "../api/auditorApi";
import { getAuthToken, getStoredAccountType } from "../auth";

function getDashboardHomeHref() {
  const accountType = getStoredAccountType();
  if (accountType === "auditor") return "/dashboard/auditor";
  if (accountType === "hospital") return "/dashboard/hospital";
  if (accountType === "enterprise") return "/dashboard/enterprise";
  return "/dashboard/admin";
}

export function SubmitAuditReportPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.auditReports.submit;
  const saveSuccess = useFormSuccess();
  const { showToast } = usePageActions();
  const [searchParams] = useSearchParams();

  const initialInvoiceId = searchParams.get("invoice_id") ?? "";
  const [invoiceId, setInvoiceId] = useState(initialInvoiceId);
  const [report, setReport] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const parsedInvoiceId = Number.parseInt(invoiceId, 10);
    if (!parsedInvoiceId || !report.trim()) {
      showToast({ message: labels.validationError, variant: "warning" });
      return;
    }

    if (!getAuthToken()) {
      saveSuccess("/dashboard/audit-reports", t.actions.saved);
      return;
    }

    setSaving(true);
    try {
      const created = await submitAuditorAuditReport({
        invoice_id: parsedInvoiceId,
        report: report.trim(),
      });
      saveSuccess(`/dashboard/audit-reports/${created.id}?source=${created.source}`, labels.successMessage);
    } catch (err) {
      showToast({
        message: err instanceof ApiError ? err.message : labels.saveError,
        variant: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Breadcrumbs
          homeHref={getDashboardHomeHref()}
          items={[
            { label: t.safecheck.auditReports.list.title, href: "/dashboard/audit-reports" },
            { label: labels.title },
          ]}
        />
        <Link
          to="/dashboard/audit-reports"
          className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
        >
          <ArrowLeft size={16} />
          {labels.backToList}
        </Link>
      </div>

      <div className="card-shadow rounded-2xl border border-border bg-card p-6">
        <h1 className="text-xl font-semibold text-foreground">{labels.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{labels.subtitle}</p>

        <form onSubmit={(event) => void handleSubmit(event)} className="mt-6 space-y-5">
          <FormField label={labels.invoiceId} htmlFor="invoice_id">
            <input
              id="invoice_id"
              type="number"
              min={1}
              value={invoiceId}
              onChange={(event) => setInvoiceId(event.target.value)}
              placeholder={labels.invoiceIdPlaceholder}
              className={inputClass}
              required
            />
          </FormField>

          <FormField label={labels.report} htmlFor="report">
            <textarea
              id="report"
              rows={12}
              value={report}
              onChange={(event) => setReport(event.target.value)}
              placeholder={labels.reportPlaceholder}
              className={textareaClass}
              required
            />
          </FormField>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            <Save size={16} />
            {saving ? labels.saving : labels.submit}
          </button>
        </form>
      </div>
    </div>
  );
}
